import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiCheckCircle, FiClock, FiUsers, FiAlertTriangle, FiLayers, FiRefreshCw, FiXCircle, FiTrash2 } from 'react-icons/fi';
import { getSuperAdminStats, getExpiringDerasars, deleteDerasarMembership } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import styles from './Dashboard.module.css';
import NoticeboardWidget from './NoticeboardWidget';

// --------------------------- Stat Card with Popup ----------------------------

const StatCardWithPopup = ({ icon, iconColor, total, label, rows }) => {
  const [open, setOpen] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (cardRef.current && !cardRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div
      className={`stat-card ${styles.clickableCard}`}
      ref={cardRef}
      onClick={() => setOpen((o) => !o)}
    >
      <div className={`stat-icon ${iconColor}`}>{icon}</div>
      <div className="stat-info">
        <h4>{total}</h4>
        <p>{label}</p>
      </div>
      {open && (
        <div className={styles.popup}>
          {rows.map((row) => (
            <div key={row.label} className={styles.popupRow}>
              <span className={styles.popupLabel}>{row.label}</span>
              <span className={styles.popupCount}>{row.count}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// --------------------------- Super Admin Dashboard ---------------------------

const SuperAdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [expiring, setExpiring] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([getSuperAdminStats(), getExpiringDerasars()])
      .then(([statsRes, expRes]) => {
        setStats(statsRes.data);
        setExpiring(expRes.data || []);
      })
      .catch(() => { })
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="loading"><div className="spinner" /></div>;

  const maxCity = stats?.derasarsByCity?.[0]?.count || 1;

  return (
    <div>
      <div className="page-header">
        <h2>Super Admin Dashboard</h2>
        <Link to="/super-admin" className="btn btn-primary btn-sm">Manage Derasars</Link>
      </div>

      {/* Expiry alert */}
      {expiring.length > 0 && (
        <div className={styles.alertBanner}>
          <div className={styles.alertIcon}><FiAlertTriangle size={20} /></div>
          <div style={{ flex: 1 }}>
            <div className={styles.alertTitle}>
              {expiring.length} derasar{expiring.length !== 1 ? 's' : ''} expiring within 30 days
            </div>
            <ul className={styles.alertList}>
              {expiring.slice(0, 5).map((d) => {
                const days = Math.ceil((new Date(d.endDate) - new Date()) / (1000 * 60 * 60 * 24));
                return (
                  <li key={d.id} className={styles.alertItem}>
                    <span>{d.name} — {d.city}</span>
                    <span className={styles.alertDays}>
                      {days === 0 ? 'Today!' : `${days}d left`}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
          <Link to="/super-admin" className="btn btn-ghost btn-sm" style={{ whiteSpace: 'nowrap' }}>
            <FiRefreshCw size={13} /> Renew
          </Link>
        </div>
      )}

      {/* Stats grid */}
      {stats && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon blue"><FiLayers /></div>
            <div className="stat-info">
              <h4>{stats.totalDerasars}</h4>
              <p>Total Derasars</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon green"><FiCheckCircle /></div>
            <div className="stat-info">
              <h4>{stats.activeDerasars}</h4>
              <p>Active</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon orange"><FiClock /></div>
            <div className="stat-info">
              <h4>{stats.pendingDerasars}</h4>
              <p>Pending Approval</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon red"><FiAlertTriangle /></div>
            <div className="stat-info">
              <h4>{stats.expiredDerasars}</h4>
              <p>Expired</p>
            </div>
          </div>
          <StatCardWithPopup
            icon={<FiUsers />}
            iconColor="blue"
            total={stats.totalUsers}
            label="Total Users"
            rows={[
              { label: 'Registered Members', count: stats.totalMembersCount },
              { label: 'Guest Members', count: stats.totalGuestsCount },
            ]}
          />
          <div className="stat-card">
            <div className="stat-icon orange"><FiAlertTriangle /></div>
            <div className="stat-info">
              <h4>{stats.expiringDerasars}</h4>
              <p>Expiring Soon</p>
            </div>
          </div>
        </div>
      )}

      <div className={styles.superAdminGrid}>
        {/* Derasars by city */}
        {stats?.derasarsByCity?.length > 0 && (
          <div className="card">
            <div className="card-header"><h3>Derasars by City</h3></div>
            <ul className={styles.cityList}>
              {stats.derasarsByCity.map((c) => (
                <li key={c.city} className={styles.cityRow}>
                  <span className={styles.cityName}>{c.city || 'Unknown'}</span>
                  <div className={styles.cityBar}>
                    <div
                      className={styles.cityBarFill}
                      style={{ width: `${(c.count / maxCity) * 100}%` }}
                    />
                  </div>
                  <span className={styles.cityCount}>{c.count}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Expiring derasars detail */}
        <div className="card">
          <div className="card-header">
            <h3>Expiring Soon</h3>
            <Link to="/super-admin" className="btn btn-ghost btn-sm">View All</Link>
          </div>
          {expiring.length === 0 ? (
            <div className="empty-state"><p>No derasars expiring in the next 30 days</p></div>
          ) : (
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Derasar</th>
                    <th>City</th>
                    <th>Expires</th>
                  </tr>
                </thead>
                <tbody>
                  {expiring.map((d) => {
                    const days = Math.ceil((new Date(d.endDate) - new Date()) / (1000 * 60 * 60 * 24));
                    return (
                      <tr key={d.id}>
                        <td>{d.name}</td>
                        <td>{d.city}</td>
                        <td style={{ color: days <= 7 ? 'var(--color-danger)' : '#b45309', fontWeight: 600 }}>
                          {days === 0 ? 'Today' : `${days}d`}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// --------------------------- Regular User Dashboard --------------------------

const UserDashboard = () => {
  const {
    user,
    activeDerasarId,
    activeDerasar,
    refreshUser,
    isActiveDerasarExpired,
    activeDerasarHasPendingRenewal,
    isDerasarAdmin,
    can,
  } = useAuth();
  const navigate = useNavigate();
  const isSuperOrPlatform = user?.role === 'super_admin' || user?.role === 'platform_admin';

  const [removeLoading, setRemoveLoading] = useState(false);

  const pendingDerasars = (user?.derasars || []).filter((d) => d.status === 'pending' && d.derasarId?.status !== 'rejected');
  const rejectedDerasars = (user?.derasars || []).filter((d) => d.derasarId?.status === 'rejected');
  const rejectedMemberships = (user?.derasars || []).filter((d) => d.status === 'rejected' && d.derasarId?.status !== 'rejected');

  const handleRemoveMembership = async (derasarId, name) => {
    if (!window.confirm(`Remove your rejected membership from "${name}"?`)) return;
    setRemoveLoading(true);
    try {
      await deleteDerasarMembership(derasarId);
      toast.success('Membership record removed');
      await refreshUser();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to remove');
    } finally {
      setRemoveLoading(false);
    }
  };

  if (!activeDerasarId) {
    const isAdmin = user?.role === 'derasar_admin';
    return (
      <div>
        <div className="page-header"><h2>Dashboard</h2></div>

        {rejectedDerasars.length > 0 ? (
          <div className={styles.rejectedCard}>
            <div className={styles.rejectedIcon}><FiXCircle size={28} /></div>
            <div>
              <div className={styles.rejectedTitle}>Registration Rejected</div>
              {rejectedDerasars.map((d) => (
                <div key={d.derasarId?.id} className={styles.rejectedItem}>
                  <div className={styles.rejectedName}>{d.derasarId?.name || 'Your Derasar'}</div>
                  {d.derasarId?.rejectionReason && (
                    <div className={styles.rejectedReason}>
                      <strong>Reason:</strong> {d.derasarId.rejectionReason}
                    </div>
                  )}
                </div>
              ))}
              <div className={styles.rejectedDesc}>
                Please contact the super admin for further assistance.
              </div>
            </div>
          </div>
        ) : pendingDerasars.length > 0 ? (
          <div className={styles.pendingCard}>
            <div className={styles.pendingIcon}><FiClock size={28} /></div>
            <div>
              <div className={styles.pendingTitle}>
                {isAdmin ? 'Registration under review' : 'Awaiting derasar approval'}
              </div>
              <div className={styles.pendingDesc}>
                {isAdmin ? (
                  <>
                    Your derasar <strong>{pendingDerasars.map((d) => d.derasarId?.name || 'registration').join(', ')}</strong> has been submitted and is pending super admin approval.
                    You will receive an email once your derasar is approved and active.
                  </>
                ) : (
                  <>Your request to join has been submitted. The derasar admin will review it shortly.</>
                )}
              </div>
              {isAdmin && (
                <div className={styles.pendingSteps}>
                  <div className={styles.pendingStep}><FiCheckCircle size={14} /> Payment received</div>
                  <div className={styles.pendingStep}><FiCheckCircle size={14} /> Registration submitted</div>
                  <div className={`${styles.pendingStep} ${styles.pendingStepActive}`}><FiClock size={14} /> Awaiting super admin approval</div>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className={styles.pendingInfo}>
            No active derasar. Browse and join a derasar to get started.
          </div>
        )}

        {rejectedMemberships.length > 0 && (
          <div className={styles.rejectedCard}>
            <div className={styles.rejectedIcon}><FiXCircle size={28} /></div>
            <div style={{ flex: 1 }}>
              <div className={styles.rejectedTitle}>Rejected Join Request{rejectedMemberships.length > 1 ? 's' : ''}</div>
              {rejectedMemberships.map((d) => (
                <div key={d.derasarId?.id || d.derasarId} className={styles.rejectedItem}>
                  <div className={styles.rejectedNameRow}>
                    <span className={styles.rejectedName}>{d.derasarId?.name || 'Derasar'}</span>
                    <button
                      className="btn btn-ghost btn-sm"
                      onClick={() => handleRemoveMembership(d.derasarId?.id || d.derasarId, d.derasarId?.name)}
                      disabled={removeLoading}
                    >
                      <FiTrash2 size={12} /> Remove
                    </button>
                  </div>
                </div>
              ))}
              <div className={styles.rejectedDesc}>Your join request was rejected. You can remove this record and try joining again.</div>
            </div>
          </div>
        )}
      </div>
    );
  }

  const isUser = user.role === 'user';

  return (
    <div>
      <div className="page-header">
        <h2>Dashboard</h2>
        <span className="badge badge-approved">
          {activeDerasar?.derasarId?.name || 'Active Derasar'}
        </span>
      </div>

      {isActiveDerasarExpired && (
        <div className={styles.rejectedCard}>
          <div className={styles.rejectedIcon}><FiAlertTriangle size={28} /></div>
          <div style={{ flex: 1 }}>
            <div className={styles.rejectedTitle}>Derasar is in read-only mode</div>
            <div className={styles.rejectedDesc}>
              The subscription for {activeDerasar?.derasarId?.name || 'this derasar'} has expired. You can still view data and reports, but changes are disabled until renewal is approved.
            </div>
            {isDerasarAdmin && !activeDerasarHasPendingRenewal && (
              <button className="btn btn-primary btn-sm" onClick={() => navigate('/subscription/renew')}>
                <FiRefreshCw size={13} /> Renew Subscription
              </button>
            )}
          </div>
        </div>
      )}

      <NoticeboardWidget derasarId={activeDerasarId} />

      <div className={styles.recentGrid}>
        <div className="card">
          <div className="card-header"><h3>Quick Actions</h3></div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {!isActiveDerasarExpired && (!isDerasarAdmin || can('offerings', 'manage')) && (
              <Link
                to="/accounting/offerings"
                state={{ autoOpenForm: true }}
                className="btn btn-primary"
              >
                + New Offering
              </Link>
            )}
            <Link to="/accounting/offerings" className="btn btn-ghost">Offerings</Link>
            {!isActiveDerasarExpired && (can('receipts', 'manage') || isSuperOrPlatform) && (
              <Link to="/accounting/receipts/new" className="btn btn-primary">+ New Receipt</Link>
            )}
            <Link to="/accounting/receipts" className="btn btn-ghost">Receipts</Link>
            {(can('payments', 'view') || isSuperOrPlatform) && (
              <Link to="/accounting/payments" className="btn btn-ghost">Payment</Link>
            )}
            <Link to="/derasar/events" className="btn btn-ghost">Events</Link>
            <Link to="/derasar/notices" className="btn btn-ghost">Notice Board</Link>
          </div>
        </div>

        <div className="card">
          <div className="card-header"><h3>{isUser ? 'My Account' : 'Accounting'}</h3></div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {isUser ? (
              <>
                <Link to="/family" className="btn btn-ghost">My Family</Link>
                <Link to="/my-persons" className="btn btn-ghost">My Persons</Link>
                <Link to="/member-reports" className="btn btn-ghost">My Reports</Link>
              </>
            ) : (
              <>
                {(can('reports', 'view') || isSuperOrPlatform) && (
                  <Link to="/member-reports" className="btn btn-ghost">Member Reports</Link>
                )}
                {(can('accountingSetup', 'view') || isSuperOrPlatform) && (
                  <Link to="/accounting/chart-of-accounts" className="btn btn-ghost">Chart of Accounts</Link>
                )}
                {(can('accountingSetup', 'view') || isSuperOrPlatform) && (
                  <Link to="/accounting/ledger" className="btn btn-ghost">Ledger Accounts</Link>
                )}
                {(can('reports', 'view') || isSuperOrPlatform) && (
                  <Link to="/accounting/reports" className="btn btn-ghost">Accounting Reports</Link>
                )}
                {(can('donationCategories', 'view') || isSuperOrPlatform) && (
                  <Link to="/management/categories" className="btn btn-ghost">Donation Categories</Link>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// --------------------------- Root Component ----------------------------------

const Dashboard = () => {
  const { user } = useAuth();
  if (user?.role === 'super_admin' || user?.role === 'platform_admin') return <SuperAdminDashboard />;
  return <UserDashboard />;
};

export default Dashboard;

